import { ActionTree } from 'vuex';
import { RestaurantState, RootState } from '../types';
import { state } from './restaurant';
import { state as authState } from '../auth/auth';
import { getMenuStyle } from '@/utils/styling';
import { getMonthFetchRange } from '@/utils/calendar';
import { getRestaurantInitialLocale } from '@/utils/i18n-helpers';
import { DateTime } from 'luxon';
import Router, { Route } from 'vue-router';
import http from '@/utils/http';
import i18n from '@/i18n';

/**
 * Filter students to only those belonging to valid restaurant IDs
 * Keeps students without restaurantId for backward compatibility
 *
 * @param {UserStudent[]} students
 * @param {string[]} validRestaurantIds
 * @return {UserStudent[]}
 */
function filterStudentsByValidLocations(students: UserStudent[], validRestaurantIds: string[]): UserStudent[] {
	return students.filter(student => {
		// Keep students without restaurantId (backward compatibility)
		if (!student.restaurantId) {
			return true;
		}
		// Keep students that belong to valid locations
		return validRestaurantIds.includes(student.restaurantId);
	});
}

export const actions: ActionTree<RestaurantState, RootState> = {
	/**
	 * We fetch the initial menus and the restaurant to gather the configuration like default
	 * tax_rate, orders paused and styling options.
	 * We then process all query parameters.
	 *
	 * @param {FetchRestaurantPayload} payload
	 * @return {Promise<void>}
	 */
	async fetchRestaurant({ commit, dispatch }, { restaurantSlug, route, router }: FetchRestaurantPayload): Promise<void> {
		try {
			// Get menus and restaurant info
			const { menuGroups, menus, restaurant } = await http.getData(restaurantSlug) as HttpPayload;
			commit('SET_RESTAURANT', restaurant);
			i18n.locale = getRestaurantInitialLocale(i18n.locale, restaurant.locales);
			commit('cart/SET_RESTAURANT_CHARGES', restaurant, { root: true });
			dispatch('auth/setAxiosInstance', restaurant, { root: true });
			commit('SET_INITIAL_MENU_GROUPS', menuGroups);
			commit('SET_INITIAL_MENUS', menus);
			dispatch('filters/setAvailableFilters', '', { root: true });

			// TODO See if we can combine those with the getUserInfo to avoid multiple requests overhead
			if (authState.authenticated) {
				await dispatch('auth/getUserSnapshot', { userId: authState.user.id, token: authState.user.token }, { root: true });
				if (authState.user.credits?.balance) {
					dispatch('cart/applyCredits', true, { root: true });
				}
			}

			// Get member cookie
			await dispatch('auth/getMemberCookie', '', { root: true });

			// Get restaurant customization
			commit('SET_CSS_LOADED', await getMenuStyle(restaurant));

			try {
				await dispatch('cart/processQueryParams', route.query, { root: true });
				// If k12 location get date range and fetch rotational menus
				if (restaurant.k12) {
					dispatch('getK12DateRange', { restaurant, route, router });
				}
			}
			catch(e) {
				const error: any = e;
				throw {
					errorMessage: error.response?.data?.message || error
				};
			}
		}
		catch (e) {
			const error: any = e;
			throw {
				error,
				errorMessage: error.errorMessage || ''
			};
		}
	},

	setRestaurant({ commit }, restaurant: Restaurant): void {
		commit('SET_RESTAURANT', restaurant);
	},

	/**
	 * Set the final filtered menus by all types (member menus, query params, etc)
	 *
	 * @param {Menu[]} filteredMenus
	 * @return {Promise}
	 */
	setFilteredMenus({ commit }, filteredMenus: Menu[]): void {
		commit('SET_FILTERED_MENUS', filteredMenus);
	},

	/**
	 * Apply member pricing discounts to items/options (sets adjustedPrice)
	 *
	 * @param {Menu[]} menus
	 * @return {Promise}
	 */
	async setMemberPrices({ commit, dispatch }, menus: Menu[]): Promise<Menu[]> {
		return await new Promise((resolve) => {
			commit('SET_MEMBER_PRICES', menus);

			// Calculate cart items adjusted prices (member discount)
			dispatch('cart/calculateCartItemsAdjustedPrices', {}, { root: true });
			resolve(state.memberMenus);
		});
	},

	/**
	 * Clear any previously applied member pricing discounts from items/options
	 *
	 * @param {Menu[]} menus
	 * @return {void}
	 */
	clearMemberPrices({ commit, dispatch }, menus: Menu[]): void {
		commit('CLEAR_MEMBER_PRICES', menus);
		dispatch('cart/calculateCartItemsAdjustedPrices', {}, { root: true });
	},

	/**
	 * Apply subsidy pricing discounts to items/options (sets adjustedPrice)
	 *
	 * @param {Menu[]} menus
	 * @return {Promise<Menu[]>}
	 */
	async setSubsidyPrices({ commit, dispatch }, menus: Menu[]): Promise<Menu[]> {
		return await new Promise((resolve) => {
			commit('SET_SUBSIDY_PRICES', menus);

			// Calculate cart items adjusted prices (subsidy discount)
			dispatch('cart/calculateCartItemsAdjustedPrices', {}, { root: true });

			// TODO - Need additional testing to ensure that subsidy works as expected for normal menus (non rotational)
			resolve(state.memberMenus);
		});
	},

	/**
	 * Get the payment config from salata of the restaurant
	 *
	 * @return {Promise}
	 */
	async getSalataRestaurantPaymentConfig({ commit }): Promise<any> {
		const payload = {
			app8RestaurantId: state.restaurant.app8_restaurant
		};

		return await new Promise((resolve, reject) => {
			authState.axiosInstance
				.post(`${process.env.VUE_APP_API_PREFIX}/payment/get-payment-config`, payload)
				.then(response => {
					commit('SET_PAYMENT_CONFIG', response.data);
					resolve(response.data);
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	/**
	 * Determine which week's menus to show based on the current date and the restaurant's order cutoff date
	 * Then fetch the rotational menus for that week
	 *
	 * @param {FetchRestaurantPayload} payload
	 * @return {Promise<void>}
	 */
	async getK12DateRange({ dispatch }, { restaurant, route, router }: { restaurant: Restaurant, route: Route, router: Router }): Promise<void> {
		// Monthly calendar view restored from the URL (selectedMonth)
		if (state.calendarViewMode === 'monthly' && state.rotationalMenuDateRange.start) {
			// Earliest orderable moment: the order cutoff if it's still ahead, otherwise now
			let earliest = DateTime.now().setZone(restaurant.zone || 'America/Toronto');
			if (restaurant.orderCutoffDate && restaurant.orderCutoffDate > earliest) {
				earliest = restaurant.orderCutoffDate;
			}

			// Fail-safe: a past month must not be reachable via the URL — snap to the earliest orderable month.
			// Parse in the restaurant zone so the month boundary matches `earliest` for out-of-zone users.
			if (DateTime.fromISO(state.rotationalMenuDateRange.start, { zone: restaurant.zone || 'America/Toronto' }).startOf('month') < earliest.startOf('month')) {
				await dispatch('setCalendarMonth', earliest.toISODate());
				router.replace({ query: { ...route.query, selectedMonth: state.rotationalMenuDateRange.start } }).catch(() => {});
			}
			else {
				await dispatch('fetchRotationalMenus');
			}
			return;
		}

		const dateRange = state.rotationalMenuDateRange;
		let now = DateTime.now().setZone(restaurant.menu_configuration?.zone || 'America/Toronto');
		let beforeOrderCutoff = false;
		const hasStartDatePassed = dateRange.start && DateTime.fromISO(dateRange.start, { zone: restaurant.menu_configuration?.zone || 'America/Toronto' }).startOf('week') < now.startOf('week');

		// If there is an order cutoff date and the current date is before the cutoff, set the current date to that
		if (restaurant.orderCutoffDate && restaurant.orderCutoffDate > now) {
			beforeOrderCutoff = true;
			now = restaurant.orderCutoffDate;
		}

		// If there is no date range or if the week has passed, set it to the start of current week and add query param
		if ((!dateRange.start || !dateRange.end) || hasStartDatePassed || beforeOrderCutoff) {
			await dispatch('setDateRange', {
				start: now.startOf('week').toISODate(),
				end: now.endOf('week').toISODate()
			});
			router.replace({ query: { ...route.query, selectedWeek: state.rotationalMenuDateRange.start } }).catch(() => {});
		}
		else {
			await dispatch('fetchRotationalMenus');
		}
	},

	/**
	 * Fetch rotational menus from the db for the selected date range
	 * Rotational menus are created in the menu manager by adding items to a menu with a specific date, which is separate from the menus in the data.json file
	 * This data isn't stored in the data.json file because it would be too much data to have to fetch in one request (approx. 200 daily menus for a school year)
	 * It also wouldn't be feasible to rebuild the data.json file and refetch it every the user changes the date range, so we fetch the rotational menus separately
	 *
	 * @return {Promise<void>}
	 */
	async fetchRotationalMenus({ commit }): Promise<void> {
		try {
			commit('SET_ROTATIONAL_MENUS_FETCHING', true);
			if (!state.rotationalMenuDateRange.start || !state.rotationalMenuDateRange.end) {
				throw i18n.t('menu.error_no_date_range');
			}
			const { data } = await authState.axiosInstance.get(`${process.env.VUE_APP_API_PREFIX}/rotational-menus?start_date=${state.rotationalMenuDateRange.start}&end_date=${state.rotationalMenuDateRange.end}&ordering_menus=true`);
			commit('SET_ROTATIONAL_MENUS', data);
		}
		catch (error) {
			throw error;
		}
		finally {
			commit('SET_ROTATIONAL_MENUS_FETCHING', false);
		}
	},

	async fetchAnnouncements({ commit }): Promise<void> {
		try {
			const { data } = await authState.axiosInstance.get(`${process.env.VUE_APP_API_PREFIX}/restaurants/${state.restaurant.id}/announcements`);
			commit('SET_ANNOUNCEMENTS', data);
		}
		catch (error) {
			throw error;
		}
	},

	async setDateRange({ commit, dispatch }, dateRange: DateRange): Promise<void> {
		commit('SET_ROTATIONAL_MENU_DATE_RANGE', dateRange);
		await dispatch('fetchRotationalMenus');
	},

	/**
	 * Switch between the weekly and monthly calendar views.
	 * Updates the displayed date range (and refetches menus) to match the
	 * selected view, anchored on the currently displayed week/month.
	 *
	 * @param {CalendarViewMode} mode
	 * @return {Promise<void>}
	 */
	async setCalendarViewMode({ commit, dispatch }, mode: CalendarViewMode): Promise<void> {
		commit('SET_CALENDAR_VIEW_MODE', mode);
		const zone = state.restaurant.zone || 'America/Toronto';
		const anchor = state.rotationalMenuDateRange.start
			? DateTime.fromISO(state.rotationalMenuDateRange.start, { zone })
			: DateTime.now().setZone(zone);

		// Earliest orderable moment: the order cutoff if it's still ahead, otherwise now.
		// The current anchor can point at a past/before-cutoff week or month (e.g. the
		// current week when the earliest orderable date is weeks away), so clamp up to
		// it and both views always land the user on a timeframe they can order in.
		const now = DateTime.now().setZone(zone);
		const earliest = state.restaurant.orderCutoffDate && state.restaurant.orderCutoffDate > now
			? state.restaurant.orderCutoffDate
			: now;
		const clampedAnchor = anchor < earliest ? earliest : anchor;

		if (mode === 'monthly') {
			await dispatch('setCalendarMonth', clampedAnchor.toISODate());
		}
		else {
			await dispatch('setDateRange', {
				start: clampedAnchor.startOf('week').toISODate(),
				end: clampedAnchor.endOf('week').toISODate()
			});
		}
	},

	/**
	 * Set the displayed month for the monthly calendar view and fetch its menus.
	 * The stored range start (first of the month) is the single source of truth
	 * for the month the calendar grid renders.
	 *
	 * @param {string} anchorISO - any ISO date within the target month
	 * @return {Promise<void>}
	 */
	async setCalendarMonth({ dispatch }, anchorISO: string): Promise<void> {
		await dispatch('setDateRange', getMonthFetchRange(anchorISO));
	},

	/**
	 * Fetch rotational menus for repeating orders (max 2 months from today)
	 *
	 * @return {Promise<void>}
	 */
	async fetchRepeatingOrdersMenus({ commit, state }): Promise<void> {
		const now = DateTime.now().setZone(state.restaurant.menu_configuration?.zone || 'America/Toronto');
		const startDate = now.startOf('day').toISODate();
		const endDate = now.plus({ months: 2 }).endOf('day').toISODate();

		const { data } = await authState.axiosInstance.get(`${process.env.VUE_APP_API_PREFIX}/rotational-menus?start_date=${startDate}&end_date=${endDate}&ordering_menus=true&repeating_orders=true`);
		commit('SET_REPEATING_ORDERS_MENUS', data);
	},

	/**
	 * Fetch locations belonging to the same restaurant group
	 * If the restaurant is not part of a group, immediately commits just the current location
	 * Also filters out students that don't belong to any of these locations
	 *
	 * @param {string} restaurantId
	 * @return {Promise<void>}
	 */
	async fetchGroupLocations({ commit }, restaurantId: string): Promise<void> {
		// If restaurant is not part of a group, just set the current location
		// TODO: Remove the restaurant.group check when all Bowlero locations are ready to use restaurant.groups
		if (!state.restaurant.group && !state.restaurant.groups?.length) {
			const currentLocationOnly = [{
				id: state.restaurant.id,
				app8_restaurant: state.restaurant.app8_restaurant,
				name: state.restaurant.name,
				slug: state.restaurant.slug,
				delivery: state.restaurant.delivery as SchoolDelivery
			}];
			commit('SET_GROUP_LOCATIONS', currentLocationOnly);

			// Filter students to only those belonging to current location
			if (authState.user.students) {
				const filteredStudents = filterStudentsByValidLocations(authState.user.students, [state.restaurant.app8_restaurant]);

				// Only update if the filtered list is different
				if (filteredStudents.length !== authState.user.students.length) {
					commit('auth/SET_USER_STUDENTS', filteredStudents, { root: true });
				}
			}

			return;
		}

		try {
			const { data } = await authState.axiosInstance.get(`${process.env.VUE_APP_API_PREFIX}/restaurants/${restaurantId}/group-locations`);
			// Add the current location to the top of the list
			if(Array.isArray(data)) {
				data.unshift({
					id: state.restaurant.id,
					app8_restaurant: state.restaurant.app8_restaurant,
					name: state.restaurant.name,
					slug: state.restaurant.slug,
					delivery: state.restaurant.delivery as SchoolDelivery
				});

				if(data.length && authState.user.students) {
					const validRestaurantIds = data.map(loc => loc.app8_restaurant);
					const filteredStudents = filterStudentsByValidLocations(authState.user.students, validRestaurantIds);

					// Only update if the filtered list is different
					if (filteredStudents.length !== authState.user.students.length) {
						commit('auth/SET_USER_STUDENTS', filteredStudents, { root: true });
					}
				}
			}
			commit('SET_GROUP_LOCATIONS', data);
		}
		catch (error) {
			throw error;
		}
	},

	setRepeatingOrdersUnavailableDates({ commit }, dates: string[]): void {
		commit('SET_REPEATING_ORDERS_UNAVAILABLE_DATES', dates);
	},

	setRepeatingOrdersSelectedMealType({ commit }, mealType: string): void {
		commit('SET_REPEATING_ORDERS_SELECTED_MEAL_TYPE', mealType);
	},

	async fetchInquiryForm({ commit }): Promise<void> {
		try {
			const { data } = await authState.axiosInstance.get<InquiryForm>(`${process.env.VUE_APP_API_PREFIX}/inquiry-forms/guest`);
			commit('SET_INQUIRY_FORM', data);
		}
		catch (error) {
			throw error;
		}
	},

	async submitInquiryForm({}, payload: InquiryFormRequestPayload): Promise<void> {
		try {
			await authState.axiosInstance.post(`${process.env.VUE_APP_API_PREFIX}/inquiry-forms/requests`, payload);
		}
		catch (error) {
			throw error;
		}
	},

	resetRestaurantState({ commit }): void {
		commit('RESET_RESTAURANT_STATE');
	}
};